import {
  AIR_MAX_DAYS_AHEAD,
  AIR_MAX_LEGS,
  AIR_MAX_PRICE_USD,
  AIR_MAX_SEATED_PASSENGERS,
  currencyExponent,
  type AirCabin,
  type AirConnectorName,
  type AirJourneyType,
  type AirOfferFilters,
  type AirSearchFailure,
  type AirSearchInput,
  type AirSearchPreferences,
  type AirSearchView,
  type Place,
} from '@bandido/shared/types';
import type { FareCountdown } from '@frontend/features/travel-services/useFareCountdown';
import { flattenPlaceOptions } from '@frontend/features/travel-services/places';
import { PROVIDER_NAMES } from '../agency/airConnection';

/**
 * Lógica pura de la búsqueda de vuelos (docs/AIR.md). Las horas de los vuelos
 * son hora local de cada aeropuerto: se leen del texto, nunca con `new Date()`
 * ni `toLocale…` (pasarían al huso del navegador — PowerFlights #260). Las
 * fechas se arman como `YYYY-MM-DD` del calendario, nunca con `toISOString()`
 * (en Uruguay, después de las 21 ya es el día siguiente en UTC).
 */

/** Un aeropuerto o ciudad elegido, con su código IATA. */
export interface AirportOption {
  code: string;
  name: string;
  kind: 'airport' | 'city';
  country: string | null;
}

export interface AirLegDraft {
  origin: AirportOption | null;
  destination: AirportOption | null;
  /** `YYYY-MM-DD` o vacío. */
  date: string;
}

export interface AirSearchDraft {
  journeyType: AirJourneyType;
  cabin: AirCabin;
  legs: AirLegDraft[];
  /** Fecha de vuelta (solo ida y vuelta): el tramo vuelve por los mismos aeropuertos. */
  returnDate: string;
  adults: number;
  children: number;
  infants: number;
  allowVirtualInterlining: boolean;
  preferences: AirSearchPreferences;
}

export function emptyLeg(origin: AirportOption | null = null): AirLegDraft {
  return { origin, destination: null, date: '' };
}

export function initialDraft(): AirSearchDraft {
  return {
    journeyType: 'RT',
    cabin: 'ECONOMY',
    legs: [emptyLeg()],
    returnDate: '',
    adults: 1,
    children: 0,
    infants: 0,
    allowVirtualInterlining: false,
    preferences: {},
  };
}

/**
 * Cambiar el tipo de viaje conserva lo cargado: ida y vuelta e ida usan un
 * tramo (la vuelta sale de la fecha de regreso); varios tramos arranca con
 * dos, y el segundo sale de donde llegó el primero.
 */
export function withJourneyType(draft: AirSearchDraft, journeyType: AirJourneyType): AirSearchDraft {
  if (journeyType === 'MD') {
    const legs = draft.legs.length >= 2 ? draft.legs : [draft.legs[0], emptyLeg(draft.legs[0].destination)];
    return { ...draft, journeyType, legs };
  }
  return { ...draft, journeyType, legs: [draft.legs[0]] };
}

export function withLeg(draft: AirSearchDraft, index: number, patch: Partial<AirLegDraft>): AirSearchDraft {
  const legs = draft.legs.map((leg, i) => (i === index ? { ...leg, ...patch } : leg));
  // En varios tramos, el siguiente sale de donde llega este si todavía no tiene origen.
  const next = legs[index + 1];
  if (draft.journeyType === 'MD' && patch.destination && next && !next.origin) legs[index + 1] = { ...next, origin: patch.destination };
  return { ...draft, legs };
}

export function addLeg(draft: AirSearchDraft): AirSearchDraft {
  if (draft.journeyType !== 'MD' || draft.legs.length >= AIR_MAX_LEGS) return draft;
  const last = draft.legs[draft.legs.length - 1];
  return { ...draft, legs: [...draft.legs, emptyLeg(last.destination)] };
}

export function removeLeg(draft: AirSearchDraft, index: number): AirSearchDraft {
  if (draft.journeyType !== 'MD' || draft.legs.length <= 2) return draft;
  return { ...draft, legs: draft.legs.filter((_, i) => i !== index) };
}

export function swapLeg(draft: AirSearchDraft, index: number): AirSearchDraft {
  const leg = draft.legs[index];
  return withLeg(draft, index, { origin: leg.destination, destination: leg.origin });
}

export type AirSearchDraftError =
  | 'airports'
  | 'sameAirport'
  | 'dates'
  | 'dateWindow'
  | 'dateOrder'
  | 'passengers'
  | 'infants'
  | 'maxPrice';

/** `YYYY-MM-DD` más `days` días de calendario. */
export function addCalendarDays(date: string, days: number): string {
  const [y, m, d] = dateParts(date);
  return new Date(Date.UTC(y, m, d + days)).toISOString().slice(0, 10);
}

/**
 * El pedido que se manda, o por qué todavía no se puede buscar. Repite las
 * reglas que vuelve a exigir client-api, para avisar antes de mandar. `today`
 * es la fecha local del asesor (`YYYY-MM-DD`).
 */
export function draftToInput(
  draft: AirSearchDraft,
  today: string,
): { input: AirSearchInput } | { error: AirSearchDraftError } {
  if (draft.legs.some((leg) => !leg.origin || !leg.destination)) return { error: 'airports' };
  if (draft.legs.some((leg) => leg.origin!.code === leg.destination!.code)) return { error: 'sameAirport' };
  const legs = draft.legs.map((leg) => ({
    origin: leg.origin!.code,
    destination: leg.destination!.code,
    departureDate: leg.date,
  }));
  if (draft.journeyType === 'RT') legs.push({ origin: legs[0].destination, destination: legs[0].origin, departureDate: draft.returnDate });
  if (legs.some((leg) => !/^\d{4}-\d{2}-\d{2}$/.test(leg.departureDate))) return { error: 'dates' };
  const latest = addCalendarDays(today, AIR_MAX_DAYS_AHEAD);
  if (legs.some((leg) => leg.departureDate < today || leg.departureDate > latest)) return { error: 'dateWindow' };
  if (legs.some((leg, i) => i > 0 && leg.departureDate < legs[i - 1].departureDate)) return { error: 'dateOrder' };
  if (draft.adults < 1 || draft.adults + draft.children > AIR_MAX_SEATED_PASSENGERS) return { error: 'passengers' };
  if (draft.infants > draft.adults) return { error: 'infants' };
  if ((draft.preferences.maxPriceUsd ?? 0) > AIR_MAX_PRICE_USD) return { error: 'maxPrice' };
  const preferences = Object.fromEntries(
    Object.entries(draft.preferences).filter(([, value]) => value !== undefined && value !== false),
  ) as AirSearchPreferences;
  return {
    input: {
      journeyType: draft.journeyType,
      cabin: draft.cabin,
      legs,
      adults: draft.adults,
      children: draft.children,
      infants: draft.infants,
      allowVirtualInterlining: draft.allowVirtualInterlining,
      ...(Object.keys(preferences).length ? { preferences } : {}),
    },
  };
}

/**
 * Opciones del picker: aeropuertos y ciudades del maestro de lugares que
 * traen código IATA (un aeropuerto puede no tenerlo cuando el código vive en
 * su ciudad, como Barajas y `MAD`: entonces se elige la ciudad).
 */
export function airportOptions(results: Place[]): AirportOption[] {
  const seen = new Set<string>();
  return flattenPlaceOptions(results)
    .filter(
      (place) => (place.kind === 'airport' || place.kind === 'city') && place.code && /^[A-Z]{3}$/.test(place.code),
    )
    .map((place) => ({
      code: place.code!,
      name: place.name,
      kind: place.kind as 'airport' | 'city',
      country: place.country?.name ?? null,
    }))
    .filter((option) => {
      const key = `${option.kind}:${option.code}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
}

export const airportLabel = (option: AirportOption): string => `${option.name} (${option.code})`;

/** "23:50" de "2026-12-12T23:50:00Z", sin convertir de zona. */
export const localTime = (at: string): string => at.slice(11, 16);

/** "2026-12-12" de una hora local. */
export const localDate = (at: string): string => at.slice(0, 10);

/** "31/10" de una fecha o una hora ("2026-10-31", "2026-10-31T23:59:00Z"), sin convertir de zona. */
export const dayMonth = (at: string): string => `${at.slice(8, 10)}/${at.slice(5, 7)}`;

/** Días de calendario entre la salida y la llegada (el "+1"), contados en fechas, no en horas. */
export function dayOffset(departureAt: string, arrivalAt: string): number {
  const from = Date.UTC(...dateParts(localDate(departureAt)));
  const to = Date.UTC(...dateParts(localDate(arrivalAt)));
  return Math.round((to - from) / 86_400_000);
}

function dateParts(date: string): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number);
  return [y, m - 1, d];
}

/** "12 h 15 m". */
export function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h ? `${h} h ${String(m).padStart(2, '0')} m` : `${m} m`;
}

/** La cuenta regresiva de una oferta contra el vencimiento que publica PowerFlights. */
export function offerCountdown(offerExpiresAt: string | null, now: number): FareCountdown {
  if (!offerExpiresAt) return { status: 'unknown', remainingMs: 0, label: '', source: 'provider', freshForHold: false };
  const remainingMs = Math.max(0, Date.parse(offerExpiresAt) - now);
  const total = Math.floor(remainingMs / 1000);
  const label = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  const status = remainingMs <= 0 ? 'expired' : remainingMs <= 3 * 60 * 1000 ? 'expiring' : 'fresh';
  // Compara el reloj del navegador con un instante absoluto: sirve para mostrar,
  // nunca para habilitar una reserva (eso lo decide el TTL local, sin desfasaje).
  return { status, remainingMs, label, source: 'provider', freshForHold: false };
}

/** La query de ofertas para client-api, sin los filtros que no se usan. */
export function offersQuery(filters: AirOfferFilters, cursor?: string): Record<string, string> {
  return {
    sort: filters.sort,
    ...(filters.maxStops !== undefined ? { maxStops: String(filters.maxStops) } : {}),
    ...(filters.airlines?.length ? { airlines: filters.airlines.join(',') } : {}),
    ...(filters.onlyRefundable ? { onlyRefundable: 'true' } : {}),
    ...(filters.virtualInterline !== undefined ? { virtualInterline: String(filters.virtualInterline) } : {}),
    ...(filters.minPrice !== undefined ? { minPrice: filters.minPrice.toFixed(2) } : {}),
    ...(filters.maxPrice !== undefined ? { maxPrice: filters.maxPrice.toFixed(2) } : {}),
    ...(cursor ? { cursor } : {}),
  };
}

export function activeFilterCount(filters: AirOfferFilters): number {
  return (
    (filters.maxStops !== undefined ? 1 : 0) +
    (filters.airlines?.length ? 1 : 0) +
    (filters.onlyRefundable ? 1 : 0) +
    (filters.virtualInterline !== undefined ? 1 : 0) +
    (filters.minPrice !== undefined || filters.maxPrice !== undefined ? 1 : 0)
  );
}

export function toggleAirline(filters: AirOfferFilters, code: string): AirOfferFilters {
  const current = filters.airlines ?? [];
  const airlines = current.includes(code) ? current.filter((c) => c !== code) : [...current, code];
  return { ...filters, airlines: airlines.length ? airlines : undefined };
}

export const failureKey = (failure: AirSearchFailure | null): string =>
  `airSearch.failure.${failure ?? 'hub_unavailable'}`;

/**
 * La clave de un error de la búsqueda: un rechazo sin código propio de
 * client-api (o una falla sin respuesta) no es de PowerFlights, así que no se
 * muestra el texto que lo culpa.
 */
export function searchErrorKey(messageKey: string, fallback: string): string {
  return ['airConnections.errors.generic', 'airConnections.errors.rejected'].includes(messageKey)
    ? fallback
    : messageKey;
}

/** "+1", "−1" o nada: la llegada en otro día de calendario que la salida. */
export function dayOffsetLabel(offset: number): string {
  if (offset === 0) return '';
  return offset > 0 ? `+${offset}` : `−${-offset}`;
}

/** Cada cuánto se pregunta por una búsqueda en curso. */
export const AIR_SEARCH_POLL_MS = 1500;

/** Se pregunta solo mientras la búsqueda sigue y el asesor no dejó de esperarla. */
export function searchPollInterval(status: AirSearchView['status'] | undefined, stopped: boolean): number | false {
  return !stopped && status === 'searching' ? AIR_SEARCH_POLL_MS : false;
}

/**
 * "USD 1.392,40": el importe de PowerFlights tal cual, solo formateado, con
 * los decimales de su moneda (JPY sin decimales, KWD con tres) y el separador
 * de `es-UY`, como `formatMoney`.
 */
export function formatAirPrice(amount: number, currency: string): string {
  const exponent = currencyExponent(currency);
  const formatted = amount.toLocaleString('es-UY', {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  });
  return `${currency} ${formatted}`;
}

/**
 * Las opciones de "Hasta N escalas": cada una cuenta las ofertas que quedan si
 * se elige, o sea las de N escalas o menos.
 */
export function stopsFilterOptions(stops: Array<{ stops: number; count: number }>) {
  let running = 0;
  return [...stops]
    .sort((a, b) => a.stops - b.stops)
    .map((entry) => {
      running += entry.count;
      return { stops: entry.stops, count: running };
    });
}

/**
 * El conector de PowerFlights de una oferta o reserva, para mostrar solo si la
 * agencia prendió "Mostrar el conector"; si no, `null`. Nunca va al voucher.
 */
export function connectorLabel(connector: string | null | undefined, showConnector: boolean): string | null {
  if (!showConnector || !connector) return null;
  return PROVIDER_NAMES[connector as AirConnectorName] ?? connector;
}

export type AirTravelerCounts = Pick<AirSearchDraft, 'adults' | 'children' | 'infants'>;
export type AirTravelerType = keyof AirTravelerCounts;

/**
 * Si se puede sumar o restar un pasajero, con las reglas de client-api: al
 * menos un adulto, hasta `AIR_MAX_SEATED_PASSENGERS` con asiento (adultos y
 * niños) y hasta un bebé por adulto.
 */
export function canStepTravelers(counts: AirTravelerCounts, type: AirTravelerType, delta: 1 | -1): boolean {
  const next = { ...counts, [type]: counts[type] + delta };
  if (next[type] < (type === 'adults' ? 1 : 0)) return false;
  if (delta < 0) return true;
  if (next.adults + next.children > AIR_MAX_SEATED_PASSENGERS) return false;
  return next.infants <= next.adults;
}

/** Suma o resta uno; un adulto menos no deja más bebés que adultos. */
export function stepTravelers(counts: AirTravelerCounts, type: AirTravelerType, delta: 1 | -1): AirTravelerCounts {
  if (!canStepTravelers(counts, type, delta)) return counts;
  const next = { ...counts, [type]: counts[type] + delta };
  return { ...next, infants: Math.min(next.infants, next.adults) };
}

/** "2 adultos, 1 niño": cada tipo que viaja, para traducir. */
export function travelersParts(counts: AirTravelerCounts): Array<{ key: string; count: number }> {
  return (['adults', 'children', 'infants'] as const)
    .filter((type) => type === 'adults' || counts[type] > 0)
    .map((type) => ({ key: `airSearch.form.count.${type}`, count: counts[type] }));
}

/** Lo elegido en preferencias, para el resumen plegado; vacío si no hay nada. */
export function preferencesParts(draft: Pick<AirSearchDraft, 'preferences' | 'allowVirtualInterlining'>) {
  const { maxStops, maxDurationMinutes, maxPriceUsd, onlyRefundable } = draft.preferences;
  const parts: Array<{ key: string; params?: Record<string, number | string> }> = [];
  if (maxStops !== undefined) parts.push({ key: 'airSearch.results.upToStops', params: { count: maxStops } });
  if (maxDurationMinutes !== undefined) parts.push({ key: 'airSearch.form.upToHours', params: { count: maxDurationMinutes / 60 } });
  if (maxPriceUsd !== undefined) parts.push({ key: 'airSearch.form.upToPrice', params: { price: formatAirPrice(maxPriceUsd, 'USD') } });
  if (onlyRefundable) parts.push({ key: 'airSearch.form.onlyRefundable' });
  if (draft.allowVirtualInterlining) parts.push({ key: 'airSearch.form.separateTickets' });
  return parts;
}
